/**
 * "Por desprogramar" — llaveros y controles dados de baja que todavía hay que
 * borrar de los motores y las puertas (2026-10-06).
 *
 * Un recurso dado de baja en el sistema SIGUE ABRIENDO el portón hasta que el
 * operador lo borra a mano de cada equipo. El sistema no toca el hardware
 * (doctrina espejo): esta lista solo recuerda qué falta, y "Marcar
 * desprogramado" registra que ya se hizo. Entran todas las bajas, vengan del
 * botón "Dar de baja", de una reposición aprobada o de datos anteriores.
 *
 * Presentacional: recibe filas y callbacks; el contenedor es Reprogramación.
 * Exportado para reuso fiel en el preview dev-only.
 */
import { useState } from 'react';
import type { RecursoPorDesprogramarRead } from '../../lib/api/types';
import { etiquetaTipoRecurso } from '../../lib/labels';
import { MOTIVO_BAJA_LABEL } from '../../lib/recursos';
import { formatearFecha } from '../../lib/tiempo';
import { Button } from '../ui/Button';

interface Props {
  items: RecursoPorDesprogramarRead[];
  puedeOperar: boolean;
  /** Devuelve un mensaje de error, o null si se registró. */
  onMarcar: (recursoId: string, nota: string | null) => Promise<string | null>;
  registrados: Set<string>;
}

export function SeccionPorDesprogramar({
  items,
  puedeOperar,
  onMarcar,
  registrados,
}: Props) {
  const [marcando, setMarcando] = useState<RecursoPorDesprogramarRead | null>(
    null,
  );
  // Las marcadas en esta sesión siguen visibles con "✓ Registrado" hasta
  // recargar, pero ya no cuentan como pendientes.
  const pendientes = items.filter((r) => !registrados.has(r.recurso_id)).length;

  return (
    <section
      className="bg-bg-elevated rounded-lg shadow overflow-hidden"
      aria-label="Llaveros y controles por desprogramar"
    >
      <div className="px-4 pt-4 pb-2">
        <h3 className="text-sm font-bold uppercase tracking-wide text-text-primary">
          Llaveros y controles por desprogramar{' '}
          <span className="text-text-secondary font-normal normal-case">
            ({pendientes})
          </span>
        </h3>
        <p className="text-xs text-text-secondary mt-0.5">
          Dados de baja (perdidos, robados, llevados o repuestos). Siguen
          abriendo hasta que los borres de los motores y las puertas.
        </p>
      </div>
      {items.length === 0 ? (
        <p className="px-4 pb-4 text-sm text-text-secondary">
          No hay llaveros ni controles pendientes de desprogramar.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-text-muted border-y border-line">
                <th className="px-4 py-2 font-medium">Casa</th>
                <th className="px-3 py-2 font-medium">Recurso</th>
                <th className="px-3 py-2 font-medium">Serial / patente</th>
                <th className="px-3 py-2 font-medium">Lo tenía</th>
                <th className="px-3 py-2 font-medium">Motivo</th>
                <th className="px-3 py-2 font-medium">Baja</th>
                {puedeOperar && <th className="px-4 py-2" />}
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.recurso_id}
                  className="border-b border-line/60 last:border-0 hover:bg-bg-page/40"
                >
                  <td className="px-4 py-2 font-semibold text-text-primary whitespace-nowrap">
                    {r.lote_identificador ?? r.ente_externo_nombre ?? '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="text-text-secondary">
                      {etiquetaTipoRecurso(r.tipo)}
                    </span>{' '}
                    <span className="font-mono text-text-primary">
                      {r.identificador}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-red-400 whitespace-nowrap">
                    {r.serial_rfid ?? r.patente ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-text-primary">
                    {r.ultimo_portador ?? (
                      <span className="text-text-muted">Sin persona</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-text-secondary">
                    {r.motivo_baja ? (
                      MOTIVO_BAJA_LABEL[r.motivo_baja]
                    ) : (
                      <span className="text-text-muted">Sin registrar</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-text-secondary whitespace-nowrap">
                    {r.baja_at ? formatearFecha(r.baja_at) : '—'}
                  </td>
                  {puedeOperar && (
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {registrados.has(r.recurso_id) ? (
                        <span className="inline-flex items-center gap-1 rounded-sm bg-emerald-600 px-2.5 py-1 text-sm font-medium text-white">
                          ✓ Registrado
                        </span>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setMarcando(r)}
                        >
                          Marcar desprogramado
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {marcando && (
        <ModalMarcarDesprogramado
          recurso={marcando}
          onCerrar={() => setMarcando(null)}
          onConfirmar={(nota) => onMarcar(marcando.recurso_id, nota)}
        />
      )}
    </section>
  );
}

function ModalMarcarDesprogramado({
  recurso,
  onCerrar,
  onConfirmar,
}: {
  recurso: RecursoPorDesprogramarRead;
  onCerrar: () => void;
  onConfirmar: (nota: string | null) => Promise<string | null>;
}) {
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirmar() {
    if (enviando) return;
    setEnviando(true);
    setError(null);
    const err = await onConfirmar(nota.trim() || null);
    setEnviando(false);
    if (err) setError(err);
    else onCerrar();
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Marcar desprogramado"
    >
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
        <h2 className="text-lg font-medium text-text-primary">
          Desprogramar {recurso.identificador}
        </h2>
        <p className="text-sm text-text-secondary">
          Confirma que ya lo borraste de <strong>todos</strong> los motores y
          puertas. El sistema no toca los equipos: solo registra que lo
          hiciste.
        </p>
        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">
            Nota <span className="text-text-muted">(opcional)</span>
          </span>
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            rows={3}
            maxLength={1000}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded-sm p-2 text-sm text-text-primary"
            placeholder="Ej: la puerta B no respondía, se reintentó."
          />
        </label>
        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCerrar} disabled={enviando}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={confirmar} disabled={enviando}>
            {enviando ? 'Registrando…' : 'Sí, ya lo borré'}
          </Button>
        </div>
      </div>
    </div>
  );
}
